import JwtBaseClient from './JwtBaseClient';

/**
 * @class AuctionProductsVdpClient
 * @description API wrapper for Auction Products VDP API
 */
class AuctionProductsVdpClient extends JwtBaseClient {
  constructor() {
    super(
      `${import.meta.env.VITE_AUCTION_PRODUCTS_VDP_BASE_URI}`,
      'response.data.errors[0].detail',
    );

    this.rootUri = 'api/v1';
  }

  /**
   * @function upsertNote
   * @description Upsert (create or update) notes for an auction
   * @param {Object} noteData - The note data
   * @param {string} [noteData.id] - Note ID (for updates)
   * @param {string} noteData.vin - Vehicle VIN
   * @param {number} noteData.sellerDealerId - Seller dealer ID
   * @param {string} noteData.note - Note content
   * @returns {Promise} API response
   */
  upsertNote(noteData, cancelToken = null) {
    return this.post(`${this.rootUri}/notes/upsert`, noteData, null, cancelToken);
  }

  /**
   * @function getNotesByAuctionIds
   * @description Fetch existing notes by auction IDs
   * @param {number[]} auctionHrIds - Array of auction HR IDs
   * @returns {Promise} API response containing notes data
   */
  getNotesByAuctionIds(auctionHrIds, cancelToken = null) {
    return this.post(`${this.rootUri}/notes/auction/listById`, {
      auctionHrIds,
    }, null, cancelToken).then((response) => response.data.data);
  }

  /**
   * @function getSellerPreferenceSettings
   * @description Fetch seller preference settings for given dealer IDs
   * @param {number[]} dealerIds - Array of dealer IDs
   * @param {Object|null} [cancelToken=null] - Optional cancel token
   * @returns {Promise} API response containing seller preference settings
   */
  getSellerPreferenceSettings(dealerIds, cancelToken = null) {
    return this.post(
      `${this.rootUri}/dealerships/fetchSellerPreferenceSettings`,
      { dealerIds },
      null,
      cancelToken,
    ).then((response) => response.data.data);
  }

  /**
   * @function upsertSellerPreferenceSettings
   * @description Upsert (create or update) seller preference settings
   * @param {Object} settingsData - The settings data
   * @param {number} settingsData.dealerId - Dealer ID
   * @param {boolean} settingsData.showSellerName - Whether to display seller name
   * @param {Object|null} [cancelToken=null] - Optional cancel token
   * @returns {Promise} API response containing updated settings
   */
  upsertSellerPreferenceSettings(settingsData, cancelToken = null) {
    return this.post(
      `${this.rootUri}/dealerships/sellerPreferenceSettings`,
      settingsData,
      null,
      cancelToken,
    ).then((response) => response.data.data);
  }

  /**
   * @function getSellerInfo
   * @description Fetch seller information for given auction IDs
   * @param {number[]} auctionIds - Array of auction IDs
   * @param {Object|null} [cancelToken=null] - Optional cancel token
   * @returns {Promise} API response containing seller info
   */
  getSellerInfo(auctionIds, cancelToken = null) {
    return this.post(
      `${this.rootUri}/auctions/sellerInfo`,
      { auctionIds },
      null,
      cancelToken,
    ).then((response) => response.data);
  }
}

export default AuctionProductsVdpClient;
